import { fetchAuthSession } from "aws-amplify/auth";

import { safeEncodeBase64 } from "./Utils";
import { Nullable, Undefinedable } from "@/api/ApiClient";
import { EmailProps } from "@components/forms/SendToEmailForm";
import { PDF_ENGINE } from "./Constants";
import { renderRouteToHtml } from "./renderRouteToHtml";
import outputs from "../../amplify_outputs.json";

type GetHtmlOptions = {
    timeoutMs?: number;
    absolutizeAssets?: boolean; // convierte href/src relativos a absolutos
};

/**
 * Obtiene el HTML autocontenido de una ruta de impresión renderizando los
 * componentes Print reales dentro de la app ya autenticada (portal same-app),
 * sin volver a arrancar la SPA. Ver `renderRouteToHtml` para el detalle.
 */
export function getHtmlFromRoute(path: string, opts: GetHtmlOptions = {}): Promise<string> {
    return renderRouteToHtml(path, opts);
}

export const generateAndUploadPdf = async (sourceURL: string, targetS3: string) => {
    const html = await getHtmlFromRoute(sourceURL);
    await sendPdfToGenerateApi(html, targetS3)
}

export type RequestEmailProps = Undefinedable<Nullable<{
    emails?: string,
    emailBody?: string
}>>

/** Endpoint de la lambda `render-pdf`, publicado por el backend en `custom.PdfApiUrl`. */
const PDF_API_URL = (
    outputs as unknown as { custom?: { PdfApiUrl?: string } }
).custom?.PdfApiUrl ?? "";

/**
 * Generador de PDF anterior: wkhtmltopdf sobre nodejs8.10, en otra cuenta y fuera de IaC,
 * con la api-key expuesta en el bundle. Se conserva solo como rollback del flag
 * `PDF_ENGINE`; ver `amplify/functions/render-pdf/`.
 */
const LEGACY_PDF_API_URL = "https://5wpoygsnfk.execute-api.us-west-2.amazonaws.com/prod/generatePdf";
const LEGACY_PDF_API_KEY = "MaeJHcOG1q2ia0P5BRPxb8xcGNrV6cST9OaDNRDg";

/** El endpoint nuevo va autenticado con el idToken de Cognito, no con una api-key. */
const buildPdfRequestHeaders = async (): Promise<Record<string, string>> => {
    if (PDF_ENGINE === "wkhtml") {
        return { "Content-Type": "application/json", "x-api-key": LEGACY_PDF_API_KEY };
    }

    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    if (!idToken) throw new Error("La sesión expiró, vuelva a iniciar sesión para generar el PDF");

    return { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` };
}

export const sendPdfToGenerateApi = async (html: string, targetFile: string, emailProps?: EmailProps): Promise<Response> => {
    let requestEmailProps: Partial<EmailProps> = {
        ...emailProps
    }

    if (requestEmailProps.emailBody) {
        requestEmailProps.emailBody = safeEncodeBase64(requestEmailProps.emailBody)
    }

    let requestBody: any = {
        data: html,
        filename: targetFile
    }
    if (emailProps) {
        requestBody.email = {
            ...requestEmailProps
        }
    }

    const url = PDF_ENGINE === "wkhtml"
        ? LEGACY_PDF_API_URL
        : `${PDF_API_URL.replace(/\/$/, "")}/generatePdf`;

    const pdfResponse = await fetch(url, {
        method: "POST",
        headers: await buildPdfRequestHeaders(),
        body: JSON.stringify(requestBody)
    });
    return pdfResponse
}
